<template>
  <div :class="campaignCounter.type">
    <div
      class="wrap-container"
      :class="{ 'with-action': campaignCounter.showAction }"
    >
      <div
        v-if="campaignCounter.title"
        class="title"
      >
        {{ campaignCounter.title }}
      </div>
      <div class="counter-wrapper">
        <div class="counter">
          <div class="item">
            <span class="number"> {{ days.toString().padStart(2, '0') }}</span>
            <span class="text">{{ $t('campaignCounter.days', null, days) }}</span>
          </div>
          <span class="separator">:</span>
          <div class="item">
            <span class="number"> {{ hours.toString().padStart(2, '0') }}</span>
            <span class="text">{{ $t('campaignCounter.hours', null, hours) }}</span>
          </div>
          <span class="separator">:</span>
          <div class="item">
            <span class="number"> {{ minutes.toString().padStart(2, '0') }}</span>
            <span class="text">{{ $t('campaignCounter.minutes', null, minutes) }}</span>
          </div>
          <span class="separator">:</span>
          <div class="item">
            <span class="number"> {{ seconds.toString().padStart(2, '0') }}</span>
            <span class="text">{{ $t('campaignCounter.seconds', null, seconds) }}</span>
          </div>
        </div>
        <a
          v-if="campaignCounter.showAction"
          class="action"
          :href="campaignCounter.action.href"
        >
          {{ campaignCounter.action.text }}
        </a>
      </div>
      <img
        v-if="campaignCounter.showIcon && isDesktop"
        :src="campaignIcon"
        :alt="$t(`campaignCounter.alt.${campaignCounter.type}`)"
        width="88"
        height="51"
        class="campaign-icon"
      />
      <close-icon
        v-if="campaignCounter.closable"
        @click="closeCounter"
      />
    </div>
  </div>
</template>

<script>
  import { mapState, mapGetters } from 'vuex'
  import CloseIcon from 'components/CloseIcon'
  import CloudIcon from 'assets/images/counter/clouds.svg'
  import CloudBlueIcon from 'assets/images/counter/clouds_blue.svg'
  import CookieUtils from 'utils/CookieUtils'
  import moment from 'moment-timezone'

  const TIMEZONE_RIGA = 'Europe/Riga'

  export default {
    components: {
      CloseIcon
    },
    data() {
      return {
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
        interval: null
      }
    },
    computed: {
      ...mapState('header', ['campaignCounter']),
      ...mapGetters('device', ['isDesktop']),
      cloudIcon() {
        return CloudIcon
      },
      cloudsBlueIcon() {
        return CloudBlueIcon
      },
      campaignIcon() {
        switch (this.campaignCounter.type) {
          case 'cherry':
            return 'https://bt-assets.airbaltic.com/images/countdown-icon-cherry.svg'
          case 'blue':
          case 'purple':
          case 'red':
            return this.cloudIcon
          default:
            return this.cloudsBlueIcon
        }
      }
    },
    mounted() {
      if (this.campaignCounter.active) {
        this.updateCountdown()
        this.interval = setInterval(this.updateCountdown, 1000)
      }
    },
    methods: {
      closeCounter() {
        CookieUtils.setCookie('campaignCounter', 'true')
        this.$store.commit('header/setCampaignCounter', { active: false })
      },
      updateCountdown() {
        const now = moment.tz(TIMEZONE_RIGA)
        const target = moment.tz(this.campaignCounter.date, TIMEZONE_RIGA)
        const duration = moment.duration(target.diff(now))

        if (duration.asSeconds() <= 0) {
          clearInterval(this.interval)
          this.days = this.hours = this.minutes = this.seconds = 0
          return
        }

        this.days = Math.floor(duration.asDays())
        this.hours = Math.floor(duration.hours())
        this.minutes = Math.floor(duration.minutes())
        this.seconds = Math.floor(duration.seconds())
      }
    }
  }
</script>

<style
  lang="scss"
  rel="stylesheet/scss"
  scoped
>
  @import '~assets/styles/common.scss';

  .default {
    background: $green-base;

    .title {
      color: $blue-dark;
    }

    .action {
      background: $blue-dark;
      color: $green-base;

      &:hover {
        background: $blue-dark-ui;
      }
    }

    .close-icon {
      color: $blue-dark;
    }
  }

  .blue {
    background: $blue-dark;

    .title {
      color: white;
    }

    .action {
      background: $green-base;
      color: $blue-dark;

      &:hover {
        background: $green-base-hover;
      }
    }

    .close-icon {
      color: white;
    }
  }

  .red {
    background: $marketing_red;

    .title {
      color: white;
    }

    .action {
      background: $blue-dark;
      color: white;

      &:hover {
        background: $blue-dark-ui;
      }
    }

    .close-icon {
      color: white;
    }
  }

  .cherry {
    background: $green-base;

    .action {
      background: $marketing_red;
      color: white;

      &:hover {
        background: $marketing_red_hover;
      }
    }

    .close-icon {
      color: $blue-dark;
    }
  }

  .purple {
    background: $marketing_purple;

    .title {
      color: white;
    }

    .action {
      background: $marketing_red;
      color: white;

      &:hover {
        background: $marketing_red_hover;
      }
    }

    .close-icon {
      color: white;
    }
  }

  .wrap-container {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    max-width: $desktop-lg;
    margin: 0 auto;
    padding: 16px;
    position: relative;
    width: 100%;

    @include bp-mf-desktop {
      padding: 16px 0;
    }

    > .title {
      display: flex;
      margin-bottom: 8px;

      @include bp-mf-tablet {
        margin: 0;
      }
    }

    @include bp-mf-tablet {
      flex-direction: row;
      align-items: center;
    }

    .title {
      font-size: rem(18, 16);
      line-height: rem(28, 16);
      font-weight: 500;

      @include bp-mf-desktop {
        font-size: rem(24, 16);
        line-height: rem(36, 16);
        max-width: 280px;
      }
    }

    &.with-action {
      justify-content: center;
      align-items: flex-start;

      @include bp-mf-tablet {
        align-items: center;
        justify-content: space-between;
      }

      .counter-wrapper {
        width: 100%;
        max-width: 340px;

        @include bp-mf-tablet {
          max-width: 700px;
        }
      }
    }
  }

  .counter {
    background: white;
    border-radius: 40px;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 24px;
    flex: 0 0 auto;

    @include bp-mf-tablet {
      gap: 24px;
      padding: 0 64px;
    }
  }

  .item {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 4px;
    gap: 4px;
    width: 32px;

    @include bp-mf-tablet {
      gap: 4px;
      width: 56px;
    }

    @include bp-mf-desktop-large {
      gap: 0;
      width: 72px;
    }
  }

  .separator {
    font-size: rem(20, 16);
    line-height: rem(20, 16);
    font-weight: 500;

    @include bp-mf-tablet {
      font-size: rem(40, 16);
      line-height: rem(40, 16);
      position: relative;
      top: -6px;
    }
  }

  .number {
    font-size: rem(14, 16);
    line-height: rem(20, 16);
    font-weight: 500;

    @include bp-mf-tablet {
      font-size: rem(28, 16);
      line-height: rem(40, 16);
    }
  }

  .text {
    font-size: rem(7, 16);
    line-height: rem(10, 16);

    @include bp-mf-tablet {
      font-size: rem(12, 16);
      line-height: rem(20, 16);
    }
  }

  .counter-wrapper {
    display: flex;
    align-items: center;
    position: relative;
  }

  .action {
    position: absolute;
    left: 224px;
    border-radius: 2px 12px 12px;
    padding: 4px 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    text-decoration: none;

    @include bp-mf-tablet {
      padding: 12px 32px;
      border-radius: 4px 24px 24px;
      left: 455px;
    }

    @include bp-mf-desktop {
      left: 480px;
    }

    @include bp-mf-desktop-large {
      left: 540px;
    }

    a {
      text-decoration: none;
      font-size: rem(12, 16);
      line-height: rem(20, 16);
      font-weight: 500;

      @include bp-mf-tablet {
        font-size: rem(14, 16);
        line-height: rem(20, 16);
      }
    }
  }

  .close-icon {
    font-size: rem(12, 16);
    right: 8px;
    top: 8px;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .campaign-icon {
    position: relative;
    right: 50px;
  }
</style>
